<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("SIDENAV.OffersManagement.AddOffer") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #266db0">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <base-image-upload-input
            col="12"
            identifier="image"
            :placeholder="$t('SIDENAV.OffersManagement.image')"
            @selectImage="selectImage"
            required
          />
          <!-- Start:: Offer Name Input Arabic -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('SIDENAV.OffersManagement.offerNameAr')"
            v-model.trim="data.name_ar"
            required
          />
          <!-- End:: Offer Name Input Arabic -->

          <!-- Start:: Offer Name Input English -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('SIDENAV.OffersManagement.offerNameEn')"
            v-model.trim="data.name_en"
            required
          />
          <!-- End:: Offer Name Input English -->

          <!-- Start:: Service Provider Input -->
          <base-select-input
            col="6"
            :optionsList="serviceProviders"
            :placeholder="$t('SIDENAV.OffersManagement.serviceProvider')"
            v-model="data.user_id"
            required
            @input="handleServiceProviderChange"
          />
          <!-- End:: Service Provider Input -->

          <!-- Start:: Product Category Input -->
          <base-select-input
            col="6"
            :optionsList="categories"
            :placeholder="$t('SIDENAV.OffersManagement.productCategory')"
            v-model="data.category_id"
            required
            @input="handleCategoryChange"
          />
          <!-- End:: Product Category Input -->

          <!-- Start:: Product Input -->
          <base-select-input
            col="6"
            :optionsList="products"
            :placeholder="$t('SIDENAV.OffersManagement.productName')"
            v-model="data.product_id"
            required
            @input="handleProductChange"
          />
          <!-- End:: Product Input -->

          <!-- Start:: Sizes Input -->
          <base-select-input
            col="6"
            :optionsList="sizes"
            :placeholder="$t('SIDENAV.OffersManagement.sizes')"
            v-model="data.size_ids"
            multiple
            required
            @input="handleSizesChange"
          />
          <!-- End:: Sizes Input -->

          <!-- Start:: Product Price Label -->
          <div class="col-12 mb-3" v-if="selectedSizesPrices?.length > 0">
            <label
              class="mb-3 font-weight-bold"
              style="color: #266db0; font-size: 16px"
              >{{ $t("SIDENAV.OffersManagement.price") }}</label
            >
            <div
              class="mb-3"
              v-for="price in selectedSizesPrices"
              :key="price.size_id"
            >
              <span class="font-weight-bold" style="color: #266db0"
                >{{ price.size_name }}:</span
              >
              {{ price.price }} {{ $t("PLACEHOLDERS.riyal") }}
            </div>
          </div>
          <!-- End:: Product Price Label -->

          <!-- Start:: Discount Percentage Input -->
          <base-input
            col="12"
            type="number"
            min="0"
            max="100"
            :placeholder="$t('SIDENAV.OffersManagement.discountPercentage')"
            v-model.number="data.discount_percentage"
            required
          />
          <!-- End:: Discount Percentage Input -->

          <!-- Start:: Discounted Price Label -->
          <div
            class="col-12 mb-3"
            v-if="data.discount_percentage && selectedSizesPrices?.length > 0"
          >
            <label
              class="mb-3 font-weight-bold"
              style="color: #266db0; font-size: 16px"
              >{{ $t("SIDENAV.OffersManagement.discountedPrice") }}</label
            >
            <div
              class="mb-3"
              v-for="price in discountedPrices"
              :key="price.size_id"
            >
              <span class="font-weight-bold" style="color: #266db0"
                >{{ price.size_name }}:</span
              >
              {{ price.discounted_price }} {{ $t("PLACEHOLDERS.riyal") }}
            </div>
          </div>
          <!-- End:: Discounted Price Label -->

          <!-- Start:: Start Date Input -->
          <base-picker-input
            col="6"
            type="date"
            :disabledDate="disabledDate"
            :placeholder="$t('SIDENAV.OffersManagement.startDate')"
            v-model="data.start_date"
            required
          />
          <!-- End:: Start Date Input -->

          <!-- Start:: End Date Input -->
          <base-picker-input
            col="6"
            type="date"
            :disabledDate="disabledDate"
            :placeholder="$t('SIDENAV.OffersManagement.endDate')"
            v-model="data.end_date"
            required
          />
          <!-- End:: End Date Input -->

          <!-- Start:: Submit Button -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button -->
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "CreateOffer",

  data() {
    return {
      isWaitingRequest: false,

      data: {
        image: {
          path: null,
          file: null,
        },
        name_ar: null,
        name_en: null,
        user_id: null,
        category_id: null,
        product_id: null,
        size_ids: [],
        discount_percentage: null,
        start_date: null,
        end_date: null,
        is_active: true,
      },

      serviceProviders: [],
      categories: [],
      products: [],
      sizes: [],
      selectedSizesPrices: [],
      discountedPrices: [],
    };
  },

  methods: {
    selectImage(selectedImage) {
      this.data.image = selectedImage;
    },
    disabledDate(current) {
      return current && current < moment().startOf("day");
    },

    async getServiceProviders() {
      try {
        const res = await this.$axios.get("providers", {
          params: {
            is_allowed: 1,
          },
        });
        this.serviceProviders = res.data.data.data.map((provider) => ({
          id: provider.id,
          name: provider.name,
        }));
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || "Error fetching service providers"
        );
      }
    },

    async handleServiceProviderChange(provider) {
      if (provider?.id) {
        try {
          const res = await this.$axios.get(`categories?user[]=${provider.id}`);
          this.categories = res.data.data.data;
          // Reset dependent fields
          this.data.category_id = null;
          this.data.product_id = null;
          this.data.size_ids = [];
          this.products = [];
          this.sizes = [];
        } catch (error) {
          this.$message.error(error.response?.data?.message);
        }
      }
    },

    async handleCategoryChange(category) {
      if (category?.id) {
        try {
          const res = await this.$axios.get(
            `products?category=${category.id}&active=1`
          );
          this.products = res.data.data.data;
          // Reset dependent fields
          this.data.product_id = null;
          this.data.size_ids = [];
          this.sizes = [];
        } catch (error) {
          this.$message.error(error.response?.data?.message);
        }
      }
    },

    async handleProductChange(product) {
      if (product?.id) {
        try {
          const selectedProduct = this.products.find(
            (p) => p.id === product.id
          );
          this.sizes = selectedProduct.sizes;
          // Reset sizes selection
          this.data.size_ids = [];
          this.selectedSizesPrices = [];
          this.discountedPrices = [];
        } catch (error) {
          this.$message.error(error.response?.data?.message);
        }
      }
    },

    handleSizesChange(selectedSizes) {
      this.selectedSizesPrices = selectedSizes.map((size) => ({
        size_id: size.id,
        size_name: size.name,
        price: size.price,
      }));

      this.calculateDiscountedPrices();
    },

    calculateDiscountedPrices() {
      if (!this.data.discount_percentage) return;

      this.discountedPrices = this.selectedSizesPrices.map((item) => ({
        size_id: item.size_id,
        size_name: item.size_name,
        discounted_price: (
          item.price *
          (1 - this.data.discount_percentage / 100)
        ).toFixed(2),
      }));
    },

    async validateFormInputs() {
      this.isWaitingRequest = true;

      try {
        const formData = new FormData();
        formData.append("file_path", this.data.image?.file);
        formData.append("name[ar]", this.data.name_ar);
        formData.append("name[en]", this.data.name_en);
        formData.append("user_id", this.data.user_id?.id);
        formData.append("category_id", this.data.category_id?.id);
        formData.append("product_id", this.data.product_id?.id);
        formData.append("discount_percentage", this.data.discount_percentage);
        this.data.size_ids.forEach((size) => {
          formData.append("size_ids[]", size.id);
        });
        formData.append("start_date", this.data.start_date);
        formData.append("end_date", this.data.end_date);
        formData.append("is_active", 1);

        await this.$axios.post("offers", formData);

        this.$message.success(this.$t("MESSAGES.addedSuccessfully"));
        this.$router.push("/offers/all");
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || "An error occurred"
        );
      } finally {
        this.isWaitingRequest = false;
      }
    },
  },

  created() {
    this.getServiceProviders();
  },

  watch: {
    "data.discount_percentage"() {
      this.calculateDiscountedPrices();
    },
  },
};
</script>
